import axios, { AxiosError } from 'axios';
import attachAuthToken from './axiosConfig';

/** CRUD das estruturas dinâmicas do checklist — rotas restritas a ADMIN. */

export interface ChecklistAdminField {
  id: string;
  key: string;
  label: string;
  helpText?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistAdminItem {
  key: string;
  label: string;
  categoryId: string;
  sortOrder: number;
  isActive: boolean;
  category: { id: string; key: string; label: string; isActive: boolean };
}

export interface ChecklistEditPermission {
  id: string;
  action: string;
  allowedRoles: string[];
}

export type ChecklistAdminEntity = 'angles' | 'categories';

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

attachAuthToken(api);

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const errMsg = (e: unknown, fallback: string) => {
  if (axios.isAxiosError(e)) {
    const ax = e as AxiosError<any>;
    const rawMsg = ax.response?.data?.message || ax.message || fallback;
    return Array.isArray(rawMsg) ? rawMsg.join(', ') : String(rawMsg);
  }
  return fallback;
};

export async function listChecklistFields(entity: ChecklistAdminEntity) {
  try {
    const { data } = await api.get(`/checklists/admin/${entity}`, { headers: authHeader() });
    return data as ChecklistAdminField[];
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao listar campos do checklist'));
  }
}

export async function createChecklistField(
  entity: ChecklistAdminEntity,
  payload: { key: string; label: string; helpText?: string; sortOrder?: number }
) {
  try {
    const { data } = await api.post(`/checklists/admin/${entity}`, payload, { headers: authHeader() });
    return data as ChecklistAdminField;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao criar campo do checklist'));
  }
}

export async function updateChecklistField(
  entity: ChecklistAdminEntity,
  id: string,
  payload: { label?: string; helpText?: string; sortOrder?: number; isActive?: boolean }
) {
  try {
    const { data } = await api.patch(`/checklists/admin/${entity}/${id}`, payload, { headers: authHeader() });
    return data as ChecklistAdminField;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao atualizar campo do checklist'));
  }
}

/**
 * Reordenação em lote de ângulos/categorias (at-50). A lista precisa trazer
 * TODOS os ids da aba, na ordem final desejada — o backend renumera 1..N.
 */
export async function reorderChecklistFields(entity: ChecklistAdminEntity, ids: string[]) {
  try {
    const { data } = await api.put(`/checklists/admin/${entity}/reorder`, { ids }, { headers: authHeader() });
    return data as ChecklistAdminField[];
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao reordenar os campos do checklist'));
  }
}

/** Reordenação em lote dos itens de uma categoria (lista completa da categoria). */
export async function reorderChecklistAdminItems(categoryId: string, keys: string[]) {
  try {
    const { data } = await api.put(
      '/checklists/admin/items/reorder',
      { categoryId, keys },
      { headers: authHeader() }
    );
    return data as ChecklistAdminItem[];
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao reordenar os itens do checklist'));
  }
}

export async function listChecklistAdminItems() {
  try {
    const { data } = await api.get('/checklists/admin/items', { headers: authHeader() });
    return data as ChecklistAdminItem[];
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao listar itens do checklist'));
  }
}

export async function createChecklistAdminItem(payload: {
  key: string;
  label: string;
  categoryId: string;
  sortOrder?: number;
}) {
  try {
    const { data } = await api.post('/checklists/admin/items', payload, { headers: authHeader() });
    return data as ChecklistAdminItem;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao criar item do checklist'));
  }
}

export async function updateChecklistAdminItem(
  key: string,
  payload: { label?: string; categoryId?: string; sortOrder?: number; isActive?: boolean }
) {
  try {
    const { data } = await api.patch(`/checklists/admin/items/${key}`, payload, { headers: authHeader() });
    return data as ChecklistAdminItem;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao atualizar item do checklist'));
  }
}

export async function listChecklistEditPermissions() {
  try {
    const { data } = await api.get('/checklists/admin/permissions', { headers: authHeader() });
    return data as ChecklistEditPermission[];
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao listar permissões do checklist'));
  }
}

export async function updateChecklistEditPermission(action: string, allowedRoles: string[]) {
  try {
    const { data } = await api.put(
      `/checklists/admin/permissions/${action}`,
      { allowedRoles },
      { headers: authHeader() }
    );
    return data as ChecklistEditPermission;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao atualizar permissão do checklist'));
  }
}

/** Template da mensagem de compartilhamento do checklist via WhatsApp (placeholders: {{link}}, {{placa}}, {{nome}}). */
export async function getChecklistShareTemplate() {
  try {
    const { data } = await api.get('/checklists/admin/share-template', { headers: authHeader() });
    return data as { value: string };
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao carregar o template da mensagem'));
  }
}

export async function updateChecklistShareTemplate(value: string) {
  try {
    const { data } = await api.put(
      '/checklists/admin/share-template',
      { value },
      { headers: authHeader() }
    );
    return data as { key: string; value: string };
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao salvar o template da mensagem'));
  }
}
